import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Mail, BookOpen } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsiveHook } from '../hooks/useResponsive';
import { contactInfo } from '../data/mockData';
import { getExperienceFormats } from '../utils/dynamicStats';
import { trackButtonClick } from '../utils/trackButtonClick';

const HeroSection = () => {
  const phoneRef = useRef(null);
  const floatingElementsRef = useRef([]);
  const { isDark, background, text, accents } = useTheme();
  const {
    isMobile,
    isTablet,
    typography,
    spacing, 
    animationDuration,
    getResponsivePadding,
    getTouchTargetSize
  } = useResponsiveHook();
  const experienceFormats = getExperienceFormats();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: animationDuration
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: animationDuration * 1.2,
        ease: "easeOut"
      }
    }
  };

  const deviceVariants = {
    hidden: { opacity: 0, scale: 0.8, rotateY: 15 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        duration: animationDuration * 1.5,
        ease: "easeOut"
      }
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (phoneRef.current) {
        const { clientX, clientY } = e;
        const { innerWidth, innerHeight } = window;
        
        const rotateX = ((clientY / innerHeight) - 0.5) * 10;
        const rotateY = ((clientX / innerWidth) - 0.5) * -10;
        
        phoneRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(50px)`;
      }

      // Float elements
      floatingElementsRef.current.forEach((el, index) => {
        if (el) {
          const speed = (index + 1) * 0.02;
          const x = Math.sin(Date.now() * speed * 0.001) * 20;
          const y = Math.cos(Date.now() * speed * 0.001) * 15;
          el.style.transform = `translate(${x}px, ${y}px)`;
        }
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Floating animation interval
    const interval = setInterval(() => {
      floatingElementsRef.current.forEach((el, index) => {
        if (el) {
          const speed = (index + 1) * 0.02;
          const x = Math.sin(Date.now() * speed * 0.001) * 20;
          const y = Math.cos(Date.now() * speed * 0.001) * 15;
          el.style.transform = `translate(${x}px, ${y}px)`;
        }
      });
    }, 50);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(interval);
    };
  }, []);

  return (
    <motion.section 
      id="hero" 
      className={`min-h-screen relative overflow-hidden ${background.primary} flex items-center justify-center transition-all duration-500`}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Floating iOS Icons - Responsive sizing */}
        <motion.div 
          ref={el => floatingElementsRef.current[0] = el}
          className={`absolute ${isMobile ? 'top-16 left-8 w-12 h-12' : 'top-20 left-20 w-16 h-16'} bg-gradient-to-br ${isDark ? 'from-blue-400 to-blue-600' : 'from-indigo-400 to-purple-500'} rounded-2xl shadow-lg ${isDark ? 'opacity-20' : 'opacity-40'}`}
          style={{ transform: 'perspective(500px) rotateX(45deg) rotateY(45deg)' }}
          animate={{
            y: [0, -10, 0],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="w-full h-full flex items-center justify-center text-white text-xl lg:text-2xl">📱</div>
        </motion.div>
        
        <motion.div 
          ref={el => floatingElementsRef.current[1] = el}
          className={`absolute ${isMobile ? 'top-32 right-16 w-10 h-10' : 'top-40 right-32 w-12 h-12'} bg-gradient-to-br ${isDark ? 'from-green-400 to-green-600' : 'from-emerald-400 to-teal-500'} rounded-xl shadow-lg ${isDark ? 'opacity-20' : 'opacity-40'}`}
          style={{ transform: 'perspective(500px) rotateX(-30deg) rotateY(30deg)' }}
          animate={{
            y: [0, -8, 0],
            x: [0, 5, 0]
          }}
          transition={{
            duration: 3.5,
            delay: 1,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="w-full h-full flex items-center justify-center text-white">⚡</div>
        </motion.div>

        <motion.div 
          ref={el => floatingElementsRef.current[2] = el}
          className={`absolute ${isMobile ? 'bottom-24 left-20 w-16 h-16' : 'bottom-32 left-40 w-20 h-20'} bg-gradient-to-br ${isDark ? 'from-purple-400 to-purple-600' : 'from-violet-400 to-pink-500'} rounded-2xl shadow-lg ${isDark ? 'opacity-20' : 'opacity-40'}`}
          style={{ transform: 'perspective(500px) rotateX(60deg) rotateY(-45deg)' }}
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, -5, 0]
          }}
          transition={{
            duration: 5,
            delay: 0.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="w-full h-full flex items-center justify-center text-white text-lg lg:text-xl">🧠</div>
        </motion.div>

        <motion.div 
          ref={el => floatingElementsRef.current[3] = el}
          className={`absolute ${isMobile ? 'bottom-16 right-12 w-12 h-12' : 'bottom-20 right-20 w-14 h-14'} bg-gradient-to-br ${isDark ? 'from-red-400 to-red-600' : 'from-rose-400 to-orange-500'} rounded-xl shadow-lg ${isDark ? 'opacity-20' : 'opacity-40'}`}
          style={{ transform: 'perspective(500px) rotateX(-45deg) rotateY(60deg)' }}
          animate={{
            y: [0, -12, 0],
            opacity: [0.4, 0.8, 0.4]
          }}
          transition={{
            duration: 3,
            delay: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="w-full h-full flex items-center justify-center text-white">🎯</div>
        </motion.div>

        {/* Grid Pattern - Responsive density */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-5' : 'opacity-10'}`}>
          <div className={`grid ${isMobile ? 'grid-cols-10' : isTablet ? 'grid-cols-15' : 'grid-cols-20'} h-full`}>
            {Array.from({ length: isMobile ? 100 : isTablet ? 225 : 400 }).map((_, i) => (
              <div key={i} className={`border ${isDark ? 'border-white/10' : 'border-gray-300/50'}`}></div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`relative z-10 text-center max-w-7xl mx-auto ${spacing.container}`}>
        <div className={`grid lg:grid-cols-2 ${isMobile ? 'gap-8' : isTablet ? 'gap-12' : 'gap-16 lg:gap-20'} items-center`}>
          {/* Left Content */}
          <motion.div 
            className={`space-y-4 lg:space-y-6 order-2 lg:order-1 ${isMobile ? 'px-2' : ''}`}
            style={{overflow: 'visible'}}
            variants={textVariants}
          >
            <div className="space-y-3" style={{overflow: 'visible'}}>
              <motion.div 
                className={`${accents.hero} font-mono ${isMobile ? 'text-sm' : 'text-lg'} tracking-wider`}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: animationDuration }}
              >
                &gt; Principal Software Engineer
              </motion.div>
              <div style={{overflow: 'visible', minHeight: isMobile ? '60px' : isTablet ? '80px' : '100px', paddingBottom: '15px'}}>
                <motion.h1 
                  className={`font-bold ${text.gradient} text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl`}
                  style={{
                    lineHeight: '1.1', 
                    paddingBottom: '0.1em', 
                    paddingTop: '0.1em', 
                    minHeight: 'fit-content', 
                    overflow: 'visible',
                    marginBottom: '10px',
                    display: 'block',
                    position: 'relative'
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ 
                    delay: 0.5, 
                    duration: animationDuration * 1.5,
                    type: "spring",
                    stiffness: 100
                  }}
                >
                  Rahul Singh
                </motion.h1>
              </div>
              <motion.p 
                className={`${typography.body} ${text.secondary} font-light leading-relaxed ${isMobile ? 'px-2' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: animationDuration }}
              >
                <span className="inline-block bg-gradient-to-r from-blue-400 via-purple-500 to-green-400 bg-clip-text text-transparent font-semibold animate-pulse-slow">
                  Shaping the Future of Apple Platforms
                </span>
                <span className="mx-3 text-gray-400">|</span>
                <span className="inline-block bg-gradient-to-r from-green-400 via-blue-500 to-purple-400 bg-clip-text text-transparent font-semibold animate-pulse-slow" style={{ animationDelay: '1s' }}>
                  {isMobile 
                    ? "Innovative iOS solutions that scale"
                    : "Innovative iOS & iPadOS & macOS solutions that scale with enterprise needs"
                  }
                </span>
              </motion.p>
            </div>

            <motion.div 
              className={`space-y-3 text-left`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0, duration: animationDuration, staggerChildren: 0.1 }}
            >
              {[
                { color: 'green', bgColor: 'bg-green-400', text: `${experienceFormats.withYears.replace(' years', '')} Years of iOS, iPadOS & macOS Expertise: Architecting and developing cutting-edge, high-performance applications.` },
                { color: 'blue', bgColor: 'bg-blue-400', text: 'Technical Leadership & Architectural Design: Spearheading complex initiatives and crafting robust, scalable software.' },
                { color: 'purple', bgColor: 'bg-purple-400', text: 'AI/ML-driven development: Utilized NLP and Core ML frameworks to enhance product functionality and user engagement.' },
                { color: 'indigo', bgColor: 'bg-indigo-400', text: 'Consistent Enterprise-Grade Delivery: Proven track record in delivering high-quality, mission-critical solutions.' },
                { color: 'orange', bgColor: 'bg-orange-400', text: 'Strategic Innovation & Business Alignment: Delivered technical solutions for Fortune 500 clients including Apple (internal tools), Kohl\'s (retail innovation), and Kellogg\'s (supply chain optimization).' },
                { color: 'red', bgColor: 'bg-red-400', text: 'Empowering Team Leadership & Mentorship: Building and guiding high-performing engineering teams.' }
              ].map((item, index) => (
                <motion.div 
                  key={index}
                  className={`flex items-start space-x-3 ${text.secondary}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 + index * 0.1, duration: animationDuration }}
                >
                  <motion.span 
                    className={`w-2 h-2 ${item.bgColor} rounded-full mt-2 flex-shrink-0`}
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.7, 1, 0.7]
                    }}
                    transition={{ 
                      duration: 2 + index * 0.5,
                      repeat: Infinity,
                      delay: index * 0.3
                    }}
                  />
                  <span className={isMobile ? 'text-sm' : 'text-base'}><strong>{item.text.split(':')[0]}:</strong> {item.text.split(':')[1]}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Social Links */}
            <motion.div 
              className="flex space-x-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: animationDuration }}
            >
              {[
                { href: contactInfo.linkedin, Icon: Linkedin, hoverColor: 'blue', label: 'LinkedIn' },
                { href: `mailto:${contactInfo.email}`, Icon: Mail, hoverColor: 'green', label: 'Email' },
                { href: "https://medium.com/@rahulsingh05", Icon: BookOpen, hoverColor: 'orange', label: 'Medium' },
                { href: "https://github.com/rahulsingh2k10", Icon: Github, hoverColor: 'purple', label: 'GitHub' }
              ].map(({ href, Icon, hoverColor, label }, index) => (
                <motion.a
                  key={index}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick({ section: 'hero', label, type: 'social_link' })}
                  className={`group ${getResponsivePadding()} ${background.card} rounded-xl transition-all duration-300 ${getTouchTargetSize()}`}
                  whileHover={{
                    scale: 1.1,
                    rotate: 2
                  }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.0 + index * 0.1 }}
                >
                  <Icon className={`w-6 h-6 ${text.muted} group-hover:text-${hoverColor}-400 transition-colors`} />
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Responsive Device Layout */}
          <motion.div 
            className={`relative flex items-center justify-center lg:justify-end ${isMobile ? 'min-h-[300px]' : isTablet ? 'min-h-[400px]' : 'min-h-[700px]'} w-full ${isMobile ? 'px-1' : 'px-2 sm:px-4 lg:px-8'} order-1 lg:order-2`}
            variants={deviceVariants}
          >
            <motion.div 
              ref={phoneRef}
              className={`relative transition-transform duration-300 ease-out ${
                isMobile ? 'scale-40' : isTablet ? 'scale-60' : 'scale-50 sm:scale-60 md:scale-70 lg:scale-85 xl:scale-100'
              } lg:ml-8`}
              style={{ transformStyle: 'preserve-3d' }}
              animate={{
                rotateY: [0, 2, -2, 0],
                y: [0, -5, 0]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {/* MacBook (Left) - Responsive positioning */}
              <div className="absolute -top-12 sm:-top-16 lg:-top-20 -left-8 sm:-left-12 md:-left-16 lg:-left-24 xl:-left-32 transform rotate-[12deg] hover:scale-105 hover:rotate-[8deg] transition-all duration-700 hover:-translate-y-2">
                <div className="relative animate-float-slow">
                  {/* MacBook Screen */}
                  <div className={`w-60 sm:w-72 lg:w-80 xl:w-96 h-40 sm:h-48 lg:h-52 xl:h-60 ${isDark ? 'bg-gray-900' : 'bg-white'} rounded-t-2xl border-4 ${isDark ? 'border-gray-700' : 'border-gray-300'} shadow-2xl overflow-hidden hover:shadow-3xl transition-shadow duration-500`}>
                    {/* macOS Menu Bar */}
                    <div className={`w-full h-6 sm:h-8 ${isDark ? 'bg-gray-800' : 'bg-gray-100'} flex items-center px-2 sm:px-4`}>
                      <div className="flex space-x-1 sm:space-x-2">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full hover:bg-red-400 transition-colors cursor-pointer"></div>
                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-500 rounded-full hover:bg-yellow-400 transition-colors cursor-pointer"></div>
                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full hover:bg-green-400 transition-colors cursor-pointer"></div>
                      </div>
                      <div className={`flex-1 text-center text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} font-medium`}>
                        Safari - iOS Development
                      </div>
                    </div>
                    
                    {/* Safari Content */}
                    <div className="p-3 sm:p-4 lg:p-6">
                      <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                        <h1 className={`text-sm sm:text-base lg:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          SwiftUI Architecture
                        </h1>
                        <div className="space-y-2 sm:space-y-3">
                          <div className={`p-2 sm:p-3 lg:p-4 ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'} rounded-lg transform hover:scale-105 transition-transform duration-300`}>
                            <h3 className={`font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm`}>
                              MVVM Pattern
                            </h3>
                            <p className={`text-xs ${isDark ? 'text-blue-200' : 'text-blue-600'} mt-1`}>
                              Modern iOS development
                            </p>
                          </div>
                          <div className={`p-2 sm:p-3 lg:p-4 ${isDark ? 'bg-green-900/30' : 'bg-green-50'} rounded-lg transform hover:scale-105 transition-transform duration-300`}>
                            <h3 className={`font-semibold ${isDark ? 'text-green-300' : 'text-green-700'} text-xs sm:text-sm`}>
                              Core Data
                            </h3>
                            <p className={`text-xs ${isDark ? 'text-green-200' : 'text-green-600'} mt-1`}>
                              Data persistence
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* MacBook Base */}
                  <div className={`w-60 sm:w-72 lg:w-80 xl:w-96 h-4 sm:h-6 ${isDark ? 'bg-gray-800' : 'bg-gray-200'} rounded-b-2xl border-4 border-t-0 ${isDark ? 'border-gray-700' : 'border-gray-300'} shadow-xl flex items-center justify-center`}>
                    <div className={`w-12 sm:w-16 lg:w-20 h-0.5 sm:h-1 ${isDark ? 'bg-gray-700' : 'bg-gray-400'} rounded-full`}></div>
                  </div>
                </div>
              </div>

              {/* iPhone (Center/Foreground) - Responsive sizing */}
              <div className="relative z-20 animate-float">
                <div className={`w-44 sm:w-56 lg:w-64 xl:w-72 h-[360px] sm:h-[450px] lg:h-[500px] xl:h-[580px] ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-200 border-gray-300'} border-4 rounded-[2.5rem] lg:rounded-[3rem] shadow-2xl overflow-hidden hover:shadow-3xl hover:scale-105 transition-all duration-500`}>
                  {/* iPhone Screen */}
                  <div className={`w-full h-full ${isDark ? 'bg-black' : 'bg-white'} rounded-[2rem] lg:rounded-[2.5rem] relative`}>
                    {/* Dynamic Island */}
                    <div className={`absolute top-2 sm:top-4 left-1/2 transform -translate-x-1/2 w-20 sm:w-28 h-4 sm:h-6 ${isDark ? 'bg-gray-900' : 'bg-black'} rounded-full animate-pulse-slow`}></div>
                    
                    {/* Status Bar */}
                    <div className="flex justify-between items-center px-4 sm:px-6 pt-8 sm:pt-12 pb-2 sm:pb-4">
                      <div className={`text-xs sm:text-sm font-semibold ${isDark ? 'text-white' : 'text-black'}`}>9:41</div>
                      <div className="flex space-x-1 items-center">
                        <div className={`w-4 sm:w-6 h-2 sm:h-3 ${isDark ? 'bg-green-500' : 'bg-green-600'} rounded-sm animate-pulse`}></div>
                      </div>
                    </div>
                    
                    {/* App Interface */}
                    <div className="px-3 sm:px-4 lg:px-6">
                      <div className="text-center mb-3 sm:mb-4 lg:mb-6">
                        <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl sm:rounded-2xl mx-auto mb-2 sm:mb-4 flex items-center justify-center shadow-lg hover:rotate-12 transition-transform duration-300`}>
                          <span className="text-white text-lg sm:text-2xl">👨‍💻</span>
                        </div>
                        <h1 className={`text-base sm:text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-1 sm:mb-2`}>
                          Dossier
                        </h1>
                        <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Principal Software Engineer
                        </p>
                      </div>
                      
                      {/* Skills Grid - Compact for mobile */}
                      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4 lg:mb-6">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-2xl p-2 sm:p-3 lg:p-4 shadow-lg hover:scale-105 transition-transform duration-300">
                          <div className="text-white text-sm sm:text-xl mb-1 sm:mb-2">⚡</div>
                          <div className="text-white font-semibold text-xs sm:text-sm">Swift 5.9</div>
                          <div className="text-blue-100 text-xs">iOS</div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-2xl p-2 sm:p-3 lg:p-4 shadow-lg hover:scale-105 transition-transform duration-300">
                          <div className="text-white text-sm sm:text-xl mb-1 sm:mb-2">🧠</div>
                          <div className="text-white font-semibold text-xs sm:text-sm">Core ML</div>
                          <div className="text-green-100 text-xs">AI/ML</div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-2xl p-2 sm:p-3 lg:p-4 shadow-lg hover:scale-105 transition-transform duration-300">
                          <div className="text-white text-sm sm:text-xl mb-1 sm:mb-2">🎨</div>
                          <div className="text-white font-semibold text-xs sm:text-sm">SwiftUI</div>
                          <div className="text-purple-100 text-xs">UI</div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-lg sm:rounded-2xl p-2 sm:p-3 lg:p-4 shadow-lg hover:scale-105 transition-transform duration-300">
                          <div className="text-white text-sm sm:text-xl mb-1 sm:mb-2">🔨</div>
                          <div className="text-white font-semibold text-xs sm:text-sm">Xcode</div>
                          <div className="text-orange-100 text-xs">IDE</div>
                        </div>
                      </div>
                      
                      {/* Action Buttons - Compact for mobile */}
                      <div className="space-y-2 sm:space-y-3">
                        <button className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold py-2 sm:py-3 lg:py-4 rounded-xl sm:rounded-2xl shadow-lg hover:scale-105 transition-transform duration-300 text-xs sm:text-sm lg:text-base">
                          View Projects
                        </button>
                        <button className={`w-full ${isDark ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-900'} font-semibold py-2 sm:py-3 lg:py-4 rounded-xl sm:rounded-2xl hover:scale-105 transition-transform duration-300 text-xs sm:text-sm lg:text-base`}>
                          Contact Me
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* iPad (Right) - Hidden on mobile to prevent overlap */}
              <div className="hidden sm:block absolute -top-4 sm:-top-8 right-4 sm:right-8 md:right-12 lg:right-16 xl:right-24 transform rotate-[-6deg] scale-75 sm:scale-85 lg:scale-95 hover:rotate-[-2deg] hover:scale-100 transition-all duration-700 hover:-translate-y-1">
                <div className={`w-36 sm:w-48 lg:w-56 xl:w-64 h-48 sm:h-64 lg:h-72 xl:h-80 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-200 border-gray-300'} border-4 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden hover:shadow-3xl transition-shadow duration-500 animate-float-reverse`}>
                  {/* iPad Screen */}
                  <div className={`w-full h-full ${isDark ? 'bg-black' : 'bg-white'} rounded-xl sm:rounded-2xl relative`}>
                    {/* Status Bar */}
                    <div className="flex justify-between items-center px-2 sm:px-4 py-2 sm:py-3">
                      <div className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-black'}`}>9:41</div>
                      <div className="flex space-x-1">
                        <div className={`w-3 sm:w-5 h-2 sm:h-3 ${isDark ? 'bg-green-500' : 'bg-green-600'} rounded-sm animate-pulse-slow`}></div>
                      </div>
                    </div>
                    
                    {/* Notes App Interface */}
                    <div className="px-2 sm:px-4 py-1 sm:py-2">
                      <div className="flex items-center justify-between mb-2 sm:mb-4">
                        <h1 className={`text-sm sm:text-lg font-bold ${isDark ? 'text-white' : 'text-black'}`}>
                          iOS Notes
                        </h1>
                        <button className="text-blue-500 text-xs sm:text-sm font-medium hover:text-blue-600 transition-colors">Edit</button>
                      </div>
                      
                      {/* Notes content - Simplified for mobile */}
                      <div className="space-y-2 sm:space-y-3">
                        <div className="transform rotate-[-0.5deg] hover:rotate-0 transition-transform duration-300">
                          <h3 className={`font-bold ${isDark ? 'text-yellow-300' : 'text-amber-600'} mb-1 sm:mb-2 text-xs sm:text-sm`}>
                            PATTERNS
                          </h3>
                          <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'} space-y-1`}>
                            <p>• MVVM with Combine</p>
                            <p>• SwiftUI architecture</p>
                          </div>
                        </div>
                        
                        <div className="transform rotate-[0.3deg] hover:rotate-0 transition-transform duration-300">
                          <h3 className={`font-bold ${isDark ? 'text-green-300' : 'text-green-600'} mb-1 sm:mb-2 text-xs sm:text-sm`}>
                            ADVANTAGES
                          </h3>
                          <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'} space-y-1`}>
                            <p>• Declarative UI</p>
                            <p>• Live previews</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements - Responsive sizing */}
              <div className="absolute -top-2 sm:-top-6 -right-2 sm:-right-6 w-4 h-4 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full animate-bounce shadow-lg shadow-blue-500/30 opacity-60 hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute -bottom-2 sm:-bottom-6 -left-2 sm:-left-6 w-3 h-3 sm:w-6 sm:h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full animate-pulse shadow-lg shadow-green-500/30 opacity-60 hover:opacity-100 transition-opacity duration-300" style={{ animationDelay: '1s' }}></div>
              <div className="hidden sm:block absolute top-1/4 -right-4 sm:-right-8 w-3 h-3 sm:w-5 sm:h-5 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full animate-ping shadow-lg shadow-purple-500/30 opacity-40 hover:opacity-80 transition-opacity duration-300"></div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Custom CSS for floating animation */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) translateZ(var(--z));
          }
          50% {
            transform: translateY(-10px) translateZ(var(--z));
          }
        }
        
        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0px) rotate(12deg);
          }
          50% {
            transform: translateY(-8px) rotate(12deg);
          }
        }
        
        @keyframes float-reverse {
          0%, 100% {
            transform: translateY(0px) rotate(-6deg);
          }
          50% {
            transform: translateY(-6px) rotate(-6deg);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        
        .animate-float-reverse {
          animation: float-reverse 5s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </motion.section>
  );
};

export default HeroSection;